import EntityManager from "@/components/EntityManager";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, today } from "@/lib/format";
import { printLeaveApplication, printLeaveRegister, printLeaveBalance } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, MessageCircle, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";

import { BRANCHES } from "@/lib/branches";
const LEAVE_TYPES = ["Casual Leave", "Sick Leave", "Earned Leave", "Loss of Pay", "Other"];
const STATUSES = ["Pending", "Approved", "Rejected"];

const STATUS_STYLE = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

const calcDays = (from, to) => {
  if (!from || !to) return 0;
  const d = (new Date(to) - new Date(from)) / 86400000 + 1;
  return d > 0 ? d : 0;
};

function LeaveBalance({ rows }) {
  const [open, setOpen] = useState(false);
  const [quotas, setQuotas] = useState({});
  const year = String(new Date().getFullYear());
  useEffect(() => {
    if (!open) return;
    api.get("/staff-master").then((r) => {
      const q = {};
      (r.data || []).forEach((s) => { if (Number(s.leave_quota) > 0) q[(s.name || "").trim().toUpperCase()] = Number(s.leave_quota); });
      setQuotas(q);
    }).catch(() => {});
  }, [open]);
  const list = useMemo(() => {
    const m = {};
    (rows || [])
      .filter((r) => (r.status || "Pending") === "Approved" && String(r.from_date || "").startsWith(year))
      .forEach((r) => {
        const a = (m[r.employee_name] = m[r.employee_name] || { name: r.employee_name, branch: r.branch || "—", types: {}, total: 0, count: 0 });
        const d = Number(r.days) || 0;
        a.total += d;
        a.count += 1;
        const t = r.leave_type || "Other";
        a.types[t] = (a.types[t] || 0) + d;
      });
    return Object.values(m)
      .map((a) => {
        const quota = quotas[(a.name || "").trim().toUpperCase()] || 0;
        return { ...a, quota, remaining: quota ? quota - a.total : null };
      })
      .sort((a, b) => b.total - a.total);
  }, [rows, year, quotas]);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="leave-balance-btn">
        <CalendarCheck2 className="mr-2 h-4 w-4" /> Leave Balance
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[760px]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 font-display">
              <span>Leave Balance — {year} (Approved)</span>
              <Button variant="outline" size="sm" className="mr-5 h-8" disabled={!list.length} onClick={() => printLeaveBalance(list, year)} data-testid="print-leave-balance-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {list.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No approved leaves in {year} yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="leave-balance-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="py-1.5">Employee</th>
                  <th className="py-1.5">Branch</th>
                  <th className="py-1.5">Break-up</th>
                  <th className="py-1.5 text-right">Quota</th>
                  <th className="py-1.5 text-right">Taken</th>
                  <th className="py-1.5 text-right">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.name} className="border-b border-slate-100" data-testid="leave-balance-row">
                    <td className="py-2 font-medium">{a.name}</td>
                    <td className="py-2">{a.branch}</td>
                    <td className="py-2 text-xs text-slate-600">{Object.entries(a.types).map(([t, d]) => `${t}: ${d}`).join(" · ")}</td>
                    <td className="py-2 text-right tabular-nums">{a.quota || "—"}</td>
                    <td className="py-2 text-right font-semibold tabular-nums">{a.total}</td>
                    <td className="py-2 text-right font-semibold tabular-nums" data-testid="leave-remaining-cell">
                      {a.remaining === null ? (
                        <span className="text-xs text-slate-400" title="Set Yearly Leave Quota in Staff Master">no quota</span>
                      ) : (
                        <span className={a.remaining < 0 ? "text-red-600" : a.remaining <= 3 ? "text-amber-600" : "text-emerald-700"}>
                          {a.remaining}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[11px] text-muted-foreground">Quota comes from "Yearly Leave Quota (days)" in Staff Master. Red = exhausted, amber = 3 or fewer days left.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

const exportLeavesCsv = (list) => {
  const headers = ["Sl No.", "Employee", "Staff Code", "Branch", "Designation", "Leave Type", "From", "To", "Days", "Status", "Applied Date", "Approved By", "Reason"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.employee_name || "", r.staff_code || "", r.branch || "", r.designation || "", r.leave_type || "", r.from_date || "", r.to_date || "", r.days || 0, r.status || "", r.applied_date || "", r.approved_by || "", r.reason || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-leave-applications-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function LeaveApplications() {
  const [staff, setStaff] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  useEffect(() => {
    api.get("/staff-master").then((r) => setStaff(r.data || [])).catch(() => {});
    api.get("/leave-applications").then((r) => setAllLeaves(r.data || [])).catch(() => {});
  }, []);

  const takenThisYear = (name) => {
    const year = String(new Date().getFullYear());
    return allLeaves
      .filter((r) => r.employee_name === name && (r.status || "Pending") === "Approved" && String(r.from_date || "").startsWith(year))
      .reduce((s, r) => s + (Number(r.days) || 0), 0);
  };

  const notifyWhatsApp = (r) => {
    const s = staff.find((x) => x.name === r.employee_name);
    const digits = String(s?.phone || "").replace(/\D/g, "");
    if (!digits) {
      toast.error("No phone number in Staff Master for this employee");
      return;
    }
    const status = (r.status || "Pending").toUpperCase();
    const period = `${r.from_date ? fmtDate(r.from_date) : ""} to ${r.to_date ? fmtDate(r.to_date) : ""} (${r.days || 0} day${Number(r.days) === 1 ? "" : "s"})`;
    const line = status === "APPROVED" ? "has been APPROVED ✅" : status === "REJECTED" ? "has been REJECTED ❌" : "is under review";
    const msg = `Dear ${r.employee_name},\n\nYour ${r.leave_type || "leave"} application for ${period} ${line}.${r.approved_by ? `\nApproved by: ${r.approved_by}` : ""}\n\n— Nisha Transport Corporation`;
    window.open(`https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <EntityManager
      title="Leave Application"
      subtitle="Record staff leave applications — apply, approve and print in the official NTC format."
      endpoint="leave-applications"
      entityName="Leave"
      selectable
      confirmSave={(form) => {
        if ((form.status || "Pending") !== "Approved" || !form.employee_name) return true;
        const s = staff.find((x) => x.name === form.employee_name);
        const quota = Number(s?.leave_quota) || 0;
        if (!quota) return true;
        const year = String(new Date().getFullYear());
        const taken = allLeaves
          .filter((r) => r.employee_name === form.employee_name && (r.status || "Pending") === "Approved" && String(r.from_date || "").startsWith(year) && r.id !== form.id)
          .reduce((sum, r) => sum + (Number(r.days) || 0), 0);
        const after = taken + (Number(form.days) || 0);
        if (after > quota) {
          return window.confirm(
            `⚠ LEAVE QUOTA EXCEEDED\n\n${form.employee_name} — yearly quota: ${quota} days\nAlready approved this year: ${taken} days\nThis leave: ${Number(form.days) || 0} days\nAfter approval: ${after} days (${after - quota} over quota)\n\nApprove anyway?`
          );
        }
        return true;
      }}
      exportCsv={exportLeavesCsv}
      headerExtra={(rows) => <LeaveBalance rows={rows} />}
      onPrintSelected={(list) => printLeaveRegister(list)}
      defaults={{ employee_name: "", staff_code: "", branch: "Chennai", designation: "", leave_type: "Casual Leave", from_date: today(), to_date: today(), days: 1, reason: "", status: "Pending", applied_date: today(), approved_by: "", remarks: "" }}
      fields={[
        {
          key: "employee_name",
          label: "Employee",
          type: "lookup",
          placeholder: "Select staff",
          options: staff.map((s) => ({ value: s.name, label: `${s.name}${s.branch ? ` — ${s.branch}` : ""}`, patch: { staff_code: s.staff_code || "", branch: s.branch || "Chennai", designation: s.designation || "" } })),
        },
        { key: "branch", label: "Branch", type: "select", options: BRANCHES },
        { key: "designation", label: "Designation", type: "text", placeholder: "Manager" },
        { key: "leave_type", label: "Leave type", type: "select", options: LEAVE_TYPES },
        { key: "from_date", label: "Leave from", type: "date" },
        { key: "to_date", label: "Leave to", type: "date" },
        { key: "days", label: "No. of days", type: "number", placeholder: "1" },
        { key: "reason", label: "Reason for leave", type: "textarea", placeholder: "Personal work / medical…" },
        { key: "status", label: "Status", type: "select", options: STATUSES },
        { key: "approved_by", label: "Approved by", type: "text", placeholder: "Manager / MD name" },
        { key: "applied_date", label: "Applied date", type: "date" },
        { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Optional notes" },
      ]}
      formExtra={(form, setForm) => {
        const d = calcDays(form.from_date, form.to_date);
        const taken = form.employee_name ? takenThisYear(form.employee_name) : 0;
        return (
          <div className="flex flex-wrap items-center gap-2">
            {form.employee_name ? (
              <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600" data-testid="taken-this-year">
                Taken this year (approved): <b>{taken}</b> day{taken === 1 ? "" : "s"}
              </span>
            ) : null}
            {d && d !== Number(form.days) ? (
              <button
                type="button"
                className="rounded border border-border px-2 py-1 text-xs text-primary hover:bg-slate-50"
                onClick={() => setForm((f) => ({ ...f, days: d }))}
                data-testid="auto-days-btn"
              >
                Set days from dates: {d}
              </button>
            ) : null}
          </div>
        );
      }}
      formExtraAfter="days"
      filters={[
        { key: "branch", label: "Branch", type: "select", options: ["All", ...BRANCHES], match: (r, v) => !v || v === "All" || (r.branch || "Chennai") === v },
        { key: "status", label: "Status", type: "select", options: ["All", ...STATUSES], match: (r, v) => !v || v === "All" || (r.status || "Pending") === v },
        { key: "date_from", label: "From", type: "date", match: (r, v) => !v || (r.from_date || "") >= v },
        { key: "date_to", label: "To", type: "date", match: (r, v) => !v || (r.from_date || "") <= v },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, i) => i + 1 },
        { key: "branch", label: "Branch", render: (r) => r.branch || "—" },
        { key: "employee_name", label: "Employee Name" },
        { key: "leave_type", label: "Type of leave", render: (r) => r.leave_type || "—" },
        { key: "from_date", label: "From", render: (r) => (r.from_date ? fmtDate(r.from_date) : "—") },
        { key: "to_date", label: "To", render: (r) => (r.to_date ? fmtDate(r.to_date) : "—") },
        { key: "days", label: "Days", align: "right", render: (r) => r.days || "—" },
        {
          key: "status",
          label: "Status",
          render: (r) => (
            <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] || STATUS_STYLE.Pending}`} data-testid="leave-status-badge">
              {r.status || "Pending"}
            </span>
          ),
        },
        { key: "applied_date", label: "Applied", render: (r) => (r.applied_date ? fmtDate(r.applied_date) : "—") },
      ]}
      rowActions={(r) => (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-emerald-600"
            title="Notify on WhatsApp"
            onClick={() => notifyWhatsApp(r)}
            data-testid="leave-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary"
            title="Print leave application"
            onClick={() => printLeaveApplication(r)}
            data-testid="print-leave-app-btn"
          >
            <Printer className="h-4 w-4" />
          </Button>
        </>
      )}
    />
  );
}
